/**
 * CSV to Markdown Table Tool
 *
 * Convert CSV to Markdown table format using papaparse.
 */

import * as Papa from 'papaparse';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

function makeAlignment(align: string, colCount: number): string[] {
  const sep: string[] = [];
  for (let i = 0; i < colCount; i++) {
    switch (align) {
      case 'center': sep.push(':---:'); break;
      case 'right': sep.push('---:'); break;
      default: sep.push('---'); break;
    }
  }
  return sep;
}

function escapeCell(value: string): string {
  return value.replace(/\|/g, '\\|').replace(/\n/g, ' ');
}

export const CsvToMarkdownTableTool: ITransformerTool = {
  id: 'csv-to-markdown-table',
  name: 'CSV → Markdown Table',
  description: 'Convert CSV to Markdown table format',
  version: '1.0.0',
  type: 'transformer',
  category: 'Converters',
  keywords: ['csv', 'markdown', 'table', 'convert', 'md'],

  seo: {
    slug: 'csv-to-markdown-table',
    pageTitle: 'CSV to Markdown Table Converter - Free Online Tool',
    metaDescription: 'Convert CSV data to Markdown table format instantly. Configurable alignment. Powered by papaparse. Fast, private, runs entirely in your browser.',
    keywords: ['csv to markdown', 'csv to md table', 'markdown table generator', 'csv converter', 'markdown table tool'],
    relatedTools: ['csv-to-json', 'csv-to-xml', 'csv-to-yaml'],
    schemaType: 'WebApplication',
  },

  supportedInputTypes: [SEMANTIC_TYPES.CSV],
  outputType: SEMANTIC_TYPES.MARKDOWN,

  attributes: [
    {
      name: 'alignment',
      type: 'select',
      label: 'Alignment',
      defaultValue: 'left',
      options: ['left', 'center', 'right'],
      description: 'Column alignment in the Markdown table',
    },
  ],

  supportsInput(_input: string, detectedType?: string): boolean {
    return detectedType === 'CSV';
  },

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    const alignment = String(config.alignment ?? 'left');

    try {
      const result = Papa.parse<string[]>(input, {
        delimiter: ',',
        header: false,
        skipEmptyLines: true,
        transform: (value) => value.trim(),
      });

      if (result.errors.length > 0) {
        throw ToolError.validation(result.errors[0]?.message ?? 'Invalid CSV');
      }

      const rows = (result.data ?? []).filter(row => row.some(cell => String(cell ?? '') !== ''));

      if (rows.length === 0) return '';

      const headers = rows[0];
      const colCount = headers.length;
      const dataRows = rows.slice(1);

      const lines: string[] = [];

      // Header row
      lines.push('| ' + headers.map(h => escapeCell(String(h ?? ''))).join(' | ') + ' |');

      // Separator row
      const sep = makeAlignment(alignment, colCount);
      lines.push('| ' + sep.join(' | ') + ' |');

      // Data rows
      for (const row of dataRows) {
        const cells: string[] = [];
        for (let i = 0; i < colCount; i++) {
          cells.push(escapeCell(String(row[i] ?? '')));
        }
        lines.push('| ' + cells.join(' | ') + ' |');
      }

      return lines.join('\n');
    } catch (e) {
      if (e instanceof ToolError) throw e;
      const error = e as Error;
      throw ToolError.validation(`Invalid CSV: ${error.message}`, {
        suggestion: 'Check that the CSV has consistent columns and proper quoting',
      });
    }
  },
};
